import { Col, Form, Input, InputNumber, Radio, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import { AuthContext } from 'components/Auth/Auth';
import ButtonLink from 'components/theme_components/ButtonLink';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import InputCurrency from 'components/InputCurrency';
import ParListSelect from 'components/ParListSelect';
import RateFormulaSelect from 'components/RateFormulaSelect';

type ProductFraisModalProps = {
  setVisible: (visible: boolean) => void;
  isCreate: boolean;
  feesList?: any;
  data?: any;
  onSave?: (fees: any) => void;
  durationmax?: number;
};

const ProductFeesModal: React.FC<ProductFraisModalProps> = ({
  setVisible,
  isCreate,
  data,
  feesList,
  durationmax = 0,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [displayRateValue, setDisplayRateValue] = useState<string | number>('');
  const { isInspect } = useContext(AuthContext);

  const calculationMethod = Form.useWatch('servcalculationmethod', form);
  const duration = Form.useWatch('servduration', form);
  const rateValue = Form.useWatch('feesrate', form);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
      });
    }
  }, [data]);

  // useEffect(() => {
  //   if (isCreate === 'create') {
  //     form.resetFields();
  //     form.setFieldsValue({
  //       servcalculationmethod: 'FIXED',
  //       servduration: 'unique',
  //     });
  //   } else if (data) {
  //     form.setFieldsValue({
  //       ...data,
  //     });
  //     if (data.servrate) {
  //       setDisplayRateValue(data.servrate);
  //     }
  //   }
  // }, [mode, data, form]);

  useEffect(() => {
    if (rateValue !== undefined) {
      setDisplayRateValue(rateValue);
    }
  }, [rateValue]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      let card;

      if (isCreate) {
        card = {
          key:
            feesList.length === 0 ? 1 : feesList[feesList.length - 1].key + 1,
          periodnumber:
            feesList.length === 0 ? 1 : feesList[feesList.length - 1].key + 1,
          servcalculationmethod: values.servcalculationmethod,
          servduration: values.servduration,
          servcategory: values.servcategory,
          servstartday: values.servstartday,
          servendday: values.servendday,
          servperiodicity: values.servperiodicity,
          servbillingday: values.servbillingday,
          tprole: values.tprole,
          servfixedamount: values.servfixedamount,
          servrateformula: values.servrateformula,
          feesrate: values.feesrate,
          servpaymenttype: values.servpaymenttype,
          rateformula: values.rateformula,
          servbasis: values.servbasis,
        };
      } else {
        card = {
          key: data.key,
          periodnumber: data.periodnumber,
          servcalculationmethod: values.servcalculationmethod,
          servduration: values.servduration,
          servcategory: values.servcategory,
          servstartday: values.servstartday,
          servendday: values.servendday,
          servperiodicity: values.servperiodicity,
          servbillingday: values.servbillingday,
          tprole: values.tprole,
          servfixedamount: values.servfixedamount,
          servrateformula: values.servrateformula,
          feesrate: values.feesrate,
          servpaymenttype: values.servpaymenttype,
          rateformula: values.rateformula,
          servbasis: values.servbasis,
        };
      }
      onSave && onSave(card);

      setVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="no-border-radius ut-wizard-form"
    >
      <Row gutter={16}>
        <Col span={12}>
          {!isInspect && (
            <FormInput
              name="servcalculationmethod"
              rules={[
                {
                  required: true,
                  message: 'Veuillez sélectionner un type de frais',
                },
              ]}
            >
              <Radio.Group>
                <Radio value="FIXED" className="ml-4">
                  <GetLabel label="fixed" />
                </Radio>
                <Radio value="PERCENT">
                  <GetLabel label="varying" />
                </Radio>
              </Radio.Group>
            </FormInput>
          )}

          {isInspect && (
            <>
              <GetLabel label="fixed" />
              <GetLabel label="varying" />
            </>
          )}
        </Col>

        <Col span={12}>
          {!isInspect && (
            <FormInput
              name="servduration"
              rules={[
                {
                  required: true,
                  message: 'Veuillez sélectionner une fréquence',
                },
              ]}
            >
              <Radio.Group>
                <Radio value="UNIQUE" className="ml-4">
                  <GetLabel label="unique" />
                </Radio>
                <Radio value="PERIODIC">
                  <GetLabel label="periodic" />
                </Radio>
              </Radio.Group>
            </FormInput>
          )}
          {isInspect && (
            <>
              <GetLabel label="unique" />
              <GetLabel label="periodic" />
            </>
          )}
        </Col>
      </Row>

      {/* Category field - always visible */}
      <Row>
        <Col span={24}>
          <FormInput
            name="servcategory"
            rules={[
              {
                required: true,
                message: 'Veuillez sélectionner une categorie de frais',
              },
            ]}
          >
            <ParListSelect parcode="SERVCAT" />
          </FormInput>
        </Col>
      </Row>

      {/* Calculation days - only for varying and periodic */}
      {calculationMethod === 'PERCENT' && duration === 'PERIODIC' && (
        <Row gutter={16}>
          <Col span={24}>
            <h4>Jours de calcul</h4>
          </Col>
          <Col span={12}>
            <FormInput
              name="servstartday"
              rules={[
                { required: true, message: 'Veuillez saisir un jour de début' },
                {
                  validator: (_, value, allValues) => {
                    const endDay = form.getFieldValue('servendday');
                    if (value < 1 || value >= durationmax) {
                      return Promise.reject(
                        new Error(
                          `Le jour de début doit être compris entre 1 et ${
                            durationmax - 1
                          }`
                        )
                      );
                    }
                    if (
                      value === undefined ||
                      endDay === undefined ||
                      value < endDay
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'Le jour de début doit être inférieur au jour de fin'
                      )
                    );
                  },
                },
              ]}
            >
              <InputNumber
                min={1}
                className="no-border-radius ut-wizard-form color-gray"
              />
            </FormInput>
          </Col>
          <Col span={12}>
            <FormInput
              name="servendday"
              rules={[
                { required: true, message: 'Veuillez saisir un jour de fin' },
                {
                  validator: (_, value) => {
                    if (value === undefined || value <= durationmax) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        `Le jour de début doit être inférieur ou égale à ${durationmax}`
                      )
                    );
                  },
                },
              ]}
            >
              <InputNumber
                min={1}
                className="no-border-radius ut-wizard-form color-gray"
              />
            </FormInput>
          </Col>
        </Row>
      )}

      {/* Periodicity - only for periodic (both fixed and varying) */}
      {duration === 'PERIODIC' && (
        <Row gutter={16}>
          <Col span={24}>
            <h4>Périodicité et facturation</h4>
          </Col>
          <Col span={12}>
            <FormInput
              name="servperiodicity"
              rules={[
                {
                  validator: (_, value) => {
                    const endday = form.getFieldValue('servendday');
                    const startday = form.getFieldValue('servstartday');
                    const billingDay = form.getFieldValue('servbillingday');
                    // if (billingDay !== undefined && value >= billingDay) {
                    //   return Promise.reject(
                    //     new Error(
                    //       `Le jour de facturation doit être inférieur à la périodicité`
                    //     )
                    //   );
                    // }
                    const isVariable =
                      form.getFieldValue('servcalculationmethod') === 'PERCENT';
                    const durationMaxValue: number =
                      form.getFieldValue('durationmax') || durationmax;
                    console.log(
                      'startday',
                      startday,
                      'endday',
                      endday,
                      'durationmax',
                      durationMaxValue
                    );
                    const days = endday - startday + 1;

                    console.log('modulo', days % value);
                    if (value > days) {
                      return Promise.reject(
                        new Error(`la periodicité > ${days} jours`)
                      );
                    }
                    console.log('isVariable', isVariable);

                    if (value === undefined || value <= 0 || value === null) {
                      return Promise.reject(
                        new Error(`Veuillez saisir une périodicité`)
                      );
                    }

                    if (isVariable) {
                      if (durationMaxValue % value === 0 && value <= days) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject(
                          new Error(
                            `la periodicité doit etre un diviseur de ${durationMaxValue}`
                          )
                        );
                      }
                    } else {
                      if (durationMaxValue % value === 0 ) {
                        return Promise.resolve();}
                       else {
                        return Promise.reject(
                          new Error(
                            `la periodicité doit etre un diviseur de ${durationMaxValue} `
                          )
                        );
                      }
                    }
                  },
                },
              ]}
            >
              <InputNumber
                min={1}
                className="no-border-radius ut-wizard-form color-gray"
                onChange={(e:any)=>{form.setFieldsValue({servbillingday:null})}}
              />
            </FormInput>
          </Col>
          <Col span={12}>
            <FormInput
              name="servbillingday"
              rules={[
                {
                  required: true,
                  message: 'Veuillez saisir le jour de facturation',
                },
                {
                  type: 'integer',
                  message: 'Le jour de facturation doit être un nombre entier',
                },
                {
                  validator: (_, value) => {
                    const periodicity = form.getFieldValue('servperiodicity');
                    console.log('periodicity', periodicity);
                    if (value < 1 || value > periodicity) {
                      return Promise.reject(
                        new Error(
                          `Le jour de facturation doit être compris entre 1 et ${periodicity}`
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                className="no-border-radius ut-wizard-form color-gray"
              />
            </FormInput>
          </Col>
        </Row>
      )}

      {/* Billing day - only for unique (both fixed and varying) */}
      {duration === 'UNIQUE' && (
        <Row>
          <Col span={24}>
            <FormInput
              name="servbillingday"
              rules={[
                {
                  required: true,
                  message: 'Veuillez saisir le jour de facturation',
                },
                {
                  type: 'integer',
                  message: 'Le jour de facturation doit être un nombre entier',
                },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                className="no-border-radius ut-wizard-form color-gray"
              />
            </FormInput>
          </Col>
        </Row>
      )}

      <Row gutter={20}>
        {/* Billing to - always visible */}
        <Col span={calculationMethod === 'PERCENT' ? 12 : 12}>
          <FormInput
            name="tprole"
            rules={[
              { required: true, message: 'Veuillez sélectionner une option' },
            ]}
          >
            <Radio.Group>
              <Radio value="OEM" className="ml-4">
                <GetLabel label="NSC" />
              </Radio>
              <Radio value="CLIENT">
                <GetLabel label="client" />
              </Radio>
            </Radio.Group>
          </FormInput>
        </Col>

        {/* Fixed amount - only for fixed (both unique and periodic) */}
        {calculationMethod === 'FIXED' && (
          <Col span={12}>
            <FormInput
              name="servfixedamount"
              rules={[
                { required: true, message: 'Veuillez saisir le montant' },
              ]}
            >
              <InputCurrency
                min={0}
                className="no-border-radius ut-wizard-form border-color-gray"
              />
            </FormInput>
          </Col>
        )}

        {/* Rate formula - only for varying (both unique and periodic) */}
        {calculationMethod === 'PERCENT' && (
          <Col span={12}>
            <FormInput
              name="rateformula"
              rules={[
                {
                  required: true,
                  message: 'Veuillez selectionner la formule',
                },
              ]}
            >
              <RateFormulaSelect
                params={{
                  formulatype: 'FLOAT',
                }}
                onSelected={(e) => {
                  // Update form values
                  const newValues = {
                    feesrate: e.ratevalue,
                    rateformula: e.formulacode,
                  };

                  form.setFieldsValue(newValues);

                  setDisplayRateValue(e.ratevalue);
                }}
              />
            </FormInput>
          </Col>
        )}
      </Row>

      {/* Rate value and payment type - only for varying (both unique and periodic) */}
      {calculationMethod === 'PERCENT' && (
        <Row gutter={20} className="d-flex align-center">
          <Col span={12}>
            <p className="bold">
              <GetLabel label="ratevalue" />: {displayRateValue}%
            </p>
            {/* Hidden form field to store the rate value */}
            <Form.Item name="feesrate" hidden>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <FormInput
              name="servbasis"
              rules={[
                {
                  required: true,
                  message: 'Veuillez sélectionner un type de paiement',
                },
              ]}
            >
              <Radio.Group>
                <Radio value="FINANCING" >
                  <GetLabel label="initial" />
                </Radio>
                <Radio value="OUTSTAND">
                  <GetLabel label="remaining" />
                </Radio>
              </Radio.Group>
            </FormInput>
          </Col>
        </Row>
      )}

      {/* Footer buttons */}
      <Row justify={'end'} className="mt-7">
        <ButtonLink
          comcode="Annuler"
          className="ut-btn-bordred"
          onClick={() => setVisible(false)}
        />
        <ButtonLink
          type="primary"
          comcode="validate"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleSubmit}
          iconPosition="end"
        />
      </Row>
    </Form>
  );
};

export default ProductFeesModal;
